import { useEffect, useState, useCallback } from 'react';
import { Loader2, X, User as UserIcon, Lock, Briefcase, Shield, Check, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage } from '@/lib/api';

interface BusinessUnit { id: string; code: string; displayName: string; isVirtual: boolean }
interface Permission { id: string; code: string; displayName: string; description: string | null; isDangerous: boolean }
interface PermissionCategory { category: string; permissions: Permission[] }

interface UserCreateResponse {
  user: {
    id: string; code: string; role: string; fullNameEn: string;
    phone: string; email: string | null;
  };
  initialPassword: string;
  mustChangePassword: boolean;
}

interface ExistingUser {
  user: { id: string; code: string; role: string; fullNameEn: string; fullNameAr: string | null;
          phone: string; email: string | null; status: string; notes: string | null; tvAccess?: boolean };
  effectivePermissions: string[];
  businessUnitCodes: string[];
  overrides: { permissionCode: string; granted: boolean }[];
}

const VALID_ROLES = [
  ['super_admin', 'Super Admin'],
  ['admin', 'Admin'],
  ['accountant', 'Accountant'],
  ['fleet_officer', 'Operations Manager'],
  ['site_manager', 'Site Manager'],
  ['construction_supervisor', 'Site Supervisor'],
  ['driver', 'Driver'],
  ['investor', 'Investor'],
  ['auditor', 'Viewer'],
  // A screen, not a person: this is the account the /tv wall signs in with,
  // and the wall refuses every other role.
  ['tv_display', 'TV Wall Display'],
] as const;

type Tab = 'personal' | 'login' | 'role' | 'permissions';

interface Props {
  mode: 'create' | 'edit';
  userId?: string;
  onClose: () => void;
  onCreated?: (user: UserCreateResponse['user'], password: string) => void;
  onSaved?: () => void;
}

export default function UserFormModal({ mode, userId, onClose, onCreated, onSaved }: Props) {
  const [tab, setTab] = useState<Tab>('personal');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [fullNameEn, setFullNameEn] = useState('');
  const [fullNameAr, setFullNameAr] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [role, setRole] = useState<string>('driver');
  const [tvAccess, setTvAccess] = useState(false);
  const [businessUnitCodes, setBusinessUnitCodes] = useState<string[]>([]);
  const [overrides, setOverrides] = useState<Map<string, boolean>>(new Map());

  // Lookups
  const [units, setUnits] = useState<BusinessUnit[]>([]);
  const [permCategories, setPermCategories] = useState<PermissionCategory[]>([]);
  const [roleDefaults, setRoleDefaults] = useState<Record<string, Set<string>>>({});

  const loadLookups = useCallback(async () => {
    try {
      const [u, p, rd] = await Promise.all([
        apiClient.get<BusinessUnit[]>('/api/v1/business-units'),
        apiClient.get<{ categories: PermissionCategory[] }>('/api/v1/permissions'),
        apiClient.get<{ roles: { role: string; permissions: string[] }[] }>('/api/v1/permissions/role-defaults'),
      ]);
      setUnits(u.data);
      setPermCategories(p.data.categories);
      const rdMap: Record<string, Set<string>> = {};
      for (const r of rd.data.roles) rdMap[r.role] = new Set(r.permissions);
      setRoleDefaults(rdMap);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }, []);

  const loadExisting = useCallback(async () => {
    if (!userId) return;
    try {
      const r = await apiClient.get<ExistingUser>('/api/v1/users/' + userId);
      setFullNameEn(r.data.user.fullNameEn);
      setFullNameAr(r.data.user.fullNameAr ?? '');
      setPhone(r.data.user.phone);
      setEmail(r.data.user.email ?? '');
      setNotes(r.data.user.notes ?? '');
      setRole(r.data.user.role);
      setTvAccess(r.data.user.tvAccess ?? false);
      setBusinessUnitCodes(r.data.businessUnitCodes);
      const m = new Map<string, boolean>();
      for (const o of r.data.overrides) m.set(o.permissionCode, o.granted);
      setOverrides(m);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }, [userId]);

  useEffect(() => {
    loadLookups();
    if (mode === 'edit') loadExisting();
  }, [loadLookups, loadExisting, mode]);

  function isEffective(code: string): boolean {
    if (overrides.has(code)) return overrides.get(code)!;
    return roleDefaults[role]?.has(code) ?? false;
  }

  function isOverridden(code: string): boolean {
    return overrides.has(code);
  }

  function toggleOverride(code: string) {
    const next = new Map(overrides);
    const def = roleDefaults[role]?.has(code) ?? false;
    if (next.has(code)) {
      next.delete(code);  // remove override → revert to role default
    } else {
      next.set(code, !def); // explicit opposite of default
    }
    setOverrides(next);
  }

  function toggleBusinessUnit(code: string) {
    setBusinessUnitCodes(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  }

  async function save() {
    setError(null);
    setSubmitting(true);
    try {
      if (mode === 'create') {
        const r = await apiClient.post<UserCreateResponse>('/api/v1/users', {
          fullNameEn: fullNameEn.trim(),
          fullNameAr: fullNameAr.trim() || undefined,
          phone: phone.trim(),
          email: email.trim() || undefined,
          role,
          businessUnitCodes,
          notes: notes.trim() || undefined,
          tvAccess,
        });
        const newUserId = r.data.user.id;
        // Apply overrides if any were set during creation
        if (overrides.size > 0) {
          await apiClient.put('/api/v1/users/' + newUserId + '/permission-overrides', {
            overrides: Array.from(overrides.entries()).map(([code, granted]) => ({ permissionCode: code, granted })),
          });
        }
        onCreated?.(r.data.user, r.data.initialPassword);
      } else {
        // Edit mode
        await apiClient.put('/api/v1/users/' + userId, {
          fullNameEn: fullNameEn.trim(),
          fullNameAr: fullNameAr.trim() || undefined,
          phone: phone.trim(),
          email: email.trim() || undefined,
          notes: notes.trim() || undefined,
          tvAccess,
        });
        await apiClient.put('/api/v1/users/' + userId + '/role', { role });
        await apiClient.put('/api/v1/users/' + userId + '/business-units', { businessUnitCodes });
        await apiClient.put('/api/v1/users/' + userId + '/permission-overrides', {
          overrides: Array.from(overrides.entries()).map(([code, granted]) => ({ permissionCode: code, granted })),
        });
        onSaved?.();
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'personal',    label: 'Personal',     icon: <UserIcon className="h-3.5 w-3.5" /> },
    { id: 'login',       label: 'Login',        icon: <Lock className="h-3.5 w-3.5" /> },
    { id: 'role',        label: 'Role & Units', icon: <Briefcase className="h-3.5 w-3.5" /> },
    { id: 'permissions', label: 'Permissions',  icon: <Shield className="h-3.5 w-3.5" /> },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card border border-border rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="border-b border-border p-4 flex items-center justify-between">
          <h2 className="text-lg font-medium">{mode === 'create' ? 'Create user' : 'Edit user'}</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>

        <div className="border-b border-border px-4 flex gap-1 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={'px-3 py-2.5 text-sm border-b-2 transition-colors -mb-px inline-flex items-center gap-1.5 whitespace-nowrap ' + (tab === t.id ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {tab === 'personal' && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label>Full name (English)</Label>
                <Input value={fullNameEn} onChange={(e) => setFullNameEn(e.target.value)} placeholder="Mohammed Al-Saud" />
              </div>
              <div className="space-y-2">
                <Label>Full name (Arabic, optional)</Label>
                <Input value={fullNameAr} onChange={(e) => setFullNameAr(e.target.value)} dir="rtl" />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+9665..." dir="ltr" className="font-mono" />
              </div>
              <div className="space-y-2">
                <Label>Email (optional)</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} dir="ltr" />
              </div>
              <div className="space-y-2">
                <Label>Notes (internal)</Label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              </div>
            </div>
          )}

          {tab === 'login' && (
            <div className="space-y-3">
              <div className="rounded-md bg-muted/30 border border-border p-3 text-sm">
                <div className="text-xs text-muted-foreground mb-1">User code (auto-generated)</div>
                <div className="font-mono text-base">{mode === 'create' ? '(assigned on save)' : '—'}</div>
              </div>
              <div className="text-sm text-muted-foreground">
                On save, a strong 12-character password is generated. The user must change it on first login.
              </div>
              <div className="rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-800 dark:text-amber-400 p-3 text-xs">
                ⚠ The password is shown only once after creation. Download the credentials sheet to save it.
              </div>
            </div>
          )}

          {tab === 'role' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Role</Label>
                <select value={role} onChange={(e) => setRole(e.target.value)}
                  className="flex h-11 w-full rounded-md border border-input bg-background px-3 text-base">
                  {VALID_ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <div className="text-xs text-muted-foreground">
                  Default permissions: {roleDefaults[role]?.size ?? 0} of {permCategories.reduce((s, c) => s + c.permissions.length, 0)}
                </div>
              </div>

              {/* JASR TV / wall access — independent of role, so any user can be
                  allowed to sign into the wall with the same credentials. */}
              <label className="flex items-start gap-3 p-3 rounded-md border border-border hover:bg-muted/30 cursor-pointer">
                <input type="checkbox" className="mt-0.5 h-4 w-4" checked={tvAccess || role === 'tv_display'} disabled={role === 'tv_display'}
                  onChange={(e) => setTvAccess(e.target.checked)} />
                <span>
                  <span className="text-sm font-medium flex items-center gap-2">
                    📺 Allow JASR TV access
                  </span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    This person can sign into the wall / TV (app.jasrhub.com/tv) with the same phone &amp; password.
                    Every TV sign-in is logged with the device it came from.
                    {role === 'tv_display' && ' (TV Wall Display accounts always have this.)'}
                  </span>
                </span>
              </label>

              <div className="space-y-2">
                <Label>Business units (data scope)</Label>
                <div className="space-y-1.5">
                  {units.map(u => (
                    <label key={u.id} className="flex items-center gap-2 p-2 rounded hover:bg-muted/30 cursor-pointer">
                      <input type="checkbox" checked={businessUnitCodes.includes(u.code)} onChange={() => toggleBusinessUnit(u.code)} />
                      <span className="text-sm font-mono text-xs text-muted-foreground">{u.code}</span>
                      <span className="text-sm">{u.displayName}</span>
                      {u.isVirtual && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-700 dark:text-violet-400">virtual</span>}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === 'permissions' && (
            <div className="space-y-4">
              <div className="text-xs text-muted-foreground">
                ✓ = effective permission. Click to toggle override (grants/revokes vs role default).
                Override is shown with • marker. {overrides.size > 0 && <strong>{overrides.size} override{overrides.size === 1 ? '' : 's'} set.</strong>}
              </div>
              {/*
                PR-PERM-FRESH — when a change actually reaches the person.
                A permission taken away is carried by their signed-in session
                until it expires, and someone who is already signed in when a
                permission is granted used to spend the rest of that hour being
                refused. Granting now takes effect immediately; revoking still
                waits, and whoever is revoking should know that.
              */}
              <div className="text-xs rounded-md border border-border bg-muted/30 px-3 py-2 text-muted-foreground">
                <strong className="text-foreground">When this takes effect:</strong>{' '}
                a permission you <em>grant</em> works straight away, even if the person is
                already signed in. A permission you <em>remove</em> stays with their current
                session for up to an hour — ask them to sign out and back in if it has to stop now.
              </div>
              {permCategories.map(cat => (
                <details key={cat.category} className="rounded-md border border-border" open>
                  <summary className="cursor-pointer px-3 py-2 text-sm font-medium bg-muted/30 hover:bg-muted/50">
                    {cat.category} <span className="text-xs text-muted-foreground">({cat.permissions.length})</span>
                  </summary>
                  <div className="divide-y divide-border">
                    {cat.permissions.map(p => {
                      const eff = isEffective(p.code);
                      const isOver = isOverridden(p.code);
                      return (
                        <button key={p.id} type="button" onClick={() => toggleOverride(p.code)}
                          className="w-full text-left px-3 py-2 hover:bg-muted/30 flex items-start gap-2 group">
                          <span className={'mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded border ' + (eff ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-400' : 'bg-muted border-border text-muted-foreground')}>
                            {eff && <Check className="h-3 w-3" />}
                          </span>
                          <div className="flex-1">
                            <div className="text-sm flex items-center gap-2">
                              {p.displayName}
                              {p.isDangerous && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-700 dark:text-rose-400">danger</span>}
                              {isOver && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-700 dark:text-violet-400">override</span>}
                            </div>
                            <div className="text-[10px] font-mono text-muted-foreground">{p.code}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </details>
              ))}
            </div>
          )}

          {error && (
            <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm inline-flex items-center gap-2 w-full">
              <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
            </div>
          )}
        </div>

        <div className="border-t border-border p-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={save} disabled={submitting || !fullNameEn.trim() || !phone.trim()} className="gap-2">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === 'create' ? 'Create user' : 'Save changes'}
          </Button>
        </div>
      </div>
    </div>
  );
}
